// Global
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Models
import { IOrderProduct } from '@/models/orders.model';

// Components
import {
  StyledListItem,
  StyledListItemContentBlock,
  StyledListItemRight,
  StyledListItemCover,
  StyledListItemName,
  StyledListItemStockQuantity,
  StyledListItemSku,
  StyledImage,
  StyledAlreadyAdded,
  StyledAmountSelectWrapper,
  StyledPriceAndQuantity,
  StyledListItemPrice,
} from './AddVariationsContainerStyles';
import { AmountSelect } from '@/components';

// Helpers
import { AmountSelectMeta } from '@/types';
import { updateProductsList, updateProductsListWithMeta } from '../../helpers';

interface AddVariationsListItemProps {
  product: IOrderProduct;
  isAlreadyAdded: boolean;
  setProductsToAdd: Dispatch<SetStateAction<{ id: string; quantity: number }[]>>;
  isLoading: boolean;
}

const AddProductListItem = ({ product, isAlreadyAdded, setProductsToAdd }: AddVariationsListItemProps) => {
  const { t } = useTranslation(['orders']);
  const [quantityValue, setQuantityValue] = useState(0);

  const handleQuantityChange = (
    value: number | null,
    meta: {
      type: AmountSelectMeta;
    },
  ) => {
    if (!value) {
      setQuantityValue(0);
      setProductsToAdd((prev) => prev.filter((el) => el.id !== product.id));
      return;
    }

    setQuantityValue((prev) => prev + (meta.type === AmountSelectMeta.DOWN ? -1 : 1));
    setProductsToAdd((prev) => updateProductsListWithMeta(prev, meta.type, product));
  };

  const handleInputQuantityChange = (value: number | null) => {
    if (value === 0) {
      setQuantityValue(0);
      setProductsToAdd((prev) => prev.filter((el) => el.id !== product.id));
      return;
    }

    setQuantityValue(value as number);
    setProductsToAdd((prev) => updateProductsList(prev, value as number, product));
  };

  return (
    <StyledListItem>
      <StyledListItemContentBlock>
        <StyledListItemCover>
          <StyledImage src={product.photo} />
        </StyledListItemCover>
        <StyledListItemRight>
          <StyledListItemSku>{product.sku}</StyledListItemSku>
          <StyledListItemName>{product.name}</StyledListItemName>
          {product?.stock && (
            <StyledListItemSku>{`${quantityValue}х ${quantityValue * +product.stock.price}₴`}</StyledListItemSku>
          )}
          <StyledPriceAndQuantity>
            <StyledListItemPrice>{product.stock?.price}₴</StyledListItemPrice>
            {isAlreadyAdded ? (
              <StyledAlreadyAdded>{t('orders_add_product_drawer_already_added')}</StyledAlreadyAdded>
            ) : (
              <StyledAmountSelectWrapper>
                <AmountSelect
                  value={quantityValue}
                  onChange={handleQuantityChange}
                  onInputChange={handleInputQuantityChange}
                  min={1}
                  max={product?.stock_quantity}
                  step={1}
                />
              </StyledAmountSelectWrapper>
            )}
          </StyledPriceAndQuantity>
          <StyledListItemStockQuantity>{`${t('orders_add_product_drawer_stock_quantity')} ${product?.stock?.region}: ${
            product.stock?.quantity
          }`}</StyledListItemStockQuantity>
        </StyledListItemRight>
      </StyledListItemContentBlock>
    </StyledListItem>
  );
};

export default AddProductListItem;
